import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { OSM_TILE_URL, OSM_ATTRIBUTION, toCoord, hasCoords, CLUSTER_OPTIONS } from '../components/map/mapUtils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { MapPin, Clock, Search, Filter, AlertTriangle, Euro, Map, List, Pencil, Trash2, Loader2 } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { isJobExpired, displayJob } from '../lib/jobs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Marker icons
const jobIcon = new L.DivIcon({
  className: 'custom-marker',
  html: '<div style="background:#1E3A8A;width:28px;height:28px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35);"></div>',
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

const urgentIcon = new L.DivIcon({
  className: 'custom-marker',
  html: '<div style="background:#EF4444;width:28px;height:28px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35);"></div>',
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

const contractorIcon = new L.DivIcon({
  className: 'custom-marker',
  html: '<div style="background:#F59E0B;width:24px;height:24px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35);"></div>',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const MapBounds = ({ markers }) => {
  const map = useMap();
  useEffect(() => {
    if (markers.length > 0) {
      const bounds = L.latLngBounds(markers.map(m => [m.lat, m.lng]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
    }
  }, [markers, map]);
  return null;
};

const JobsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [viewMode, setViewMode] = useState('list');
  const [showContractors, setShowContractors] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  
  const [filters, setFilters] = useState({
    trade: '',
    city: '',
    country: '',
    is_urgent: false,
    budget_min: '',
    budget_max: ''
  });

  useEffect(() => {
    axios.get(`${API}/trades`).then(res => setTrades(res.data)).catch(() => {});
  }, []);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.trade) params.append('trade', filters.trade);
      if (filters.city) params.append('city', filters.city);
      if (filters.country) params.append('country', filters.country);
      if (filters.is_urgent) params.append('is_urgent', 'true');
      if (filters.budget_min) params.append('budget_min', filters.budget_min);
      if (filters.budget_max) params.append('budget_max', filters.budget_max);
      if (viewMode === 'map') params.append('limit', '500');

      const res = await axios.get(`${API}/jobs?${params.toString()}`);
      setJobs(res.data.jobs || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, viewMode]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  useEffect(() => {
    if (showContractors && viewMode === 'map') {
      axios.get(`${API}/map/data`).then(res => {
        setContractors(res.data.contractors || []);
      }).catch(() => {});
    }
  }, [showContractors, viewMode]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ trade: '', city: '', country: '', is_urgent: false, budget_min: '', budget_max: '' });
  };

  const handleDelete = async (jobId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Supprimer ce chantier ? Cette action est irréversible.')) return;
    setDeletingId(jobId);
    try {
      await axios.get(`${API}/jobs/${jobId}/delete`, { withCredentials: true });
      toast.success('Chantier supprimé');
      setJobs(prev => prev.filter(j => j.id !== jobId));
      setTotal(prev => prev - 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeletingId(null);
    }
  };

  const handleEdit = (jobId, e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/dashboard/edit-job/${jobId}`);
  };

  // Build map markers
  const markers = [];
  jobs.forEach(job => {
    if (hasCoords(job)) {
      markers.push({ lat: toCoord(job.latitude), lng: toCoord(job.longitude), type: 'job', data: job });
    }
  });
  if (showContractors) {
    contractors.forEach(c => {
      if (hasCoords(c)) {
        markers.push({ lat: toCoord(c.latitude), lng: toCoord(c.longitude), type: 'contractor', data: c });
      }
    });
  }

  const isOwner = (job) => user && (job.company_id === user.id || user.role === 'admin');

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8">
      <SEOHead
        title="Chantiers BTP - Trouvez des missions de sous-traitance"
        description="Parcourez les chantiers disponibles en Belgique, Luxembourg et France. Maçonnerie, toiture, plomberie, électricité et plus. Postulez directement en ligne."
        path="/jobs"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <p className="text-overline mb-2">{t('jobs.title')}</p>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl text-[#111827] tracking-tight">
              {t('jobs.title')}
            </h1>
            <p className="text-[#4B5563] mt-2">{total} chantiers disponibles</p>
          </div>
          {/* View Toggle */}
          <div className="flex items-center gap-2 bg-white border rounded-lg p-1" data-testid="view-toggle">
            <Button 
              variant={viewMode === 'list' ? 'default' : 'ghost'} 
              size="sm" 
              onClick={() => setViewMode('list')}
              className={viewMode === 'list' ? 'bg-[#1E3A8A] text-white' : ''}
              data-testid="view-list-btn"
            >
              <List className="w-4 h-4 mr-1" /> Liste
            </Button>
            <Button 
              variant={viewMode === 'map' ? 'default' : 'ghost'} 
              size="sm" 
              onClick={() => setViewMode('map')}
              className={viewMode === 'map' ? 'bg-[#1E3A8A] text-white' : ''}
              data-testid="view-map-btn"
            >
              <Map className="w-4 h-4 mr-1" /> Carte
            </Button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <aside className="lg:w-72 flex-shrink-0">
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading font-bold text-lg flex items-center gap-2">
                    <Filter className="w-5 h-5" />
                    {t('common.filter')}
                  </h3>
                  <Button variant="ghost" size="sm" onClick={clearFilters}>Effacer</Button>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('jobs.trade')}</label>
                    <Select value={filters.trade || "all"} onValueChange={(v) => handleFilterChange('trade', v === "all" ? '' : v)}>
                      <SelectTrigger data-testid="filter-trade"><SelectValue placeholder="Tous les métiers" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous les métiers</SelectItem>
                        {trades.map(trade => (
                          <SelectItem key={trade.id} value={trade.id}>{t(`trades.${trade.id}`) || trade.name_fr}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('jobs.city')}</label>
                    <Input placeholder="Rechercher une ville..." value={filters.city} onChange={(e) => handleFilterChange('city', e.target.value)} className="input-field" data-testid="filter-city" />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('jobs.country')}</label>
                    <Select value={filters.country || "all"} onValueChange={(v) => handleFilterChange('country', v === "all" ? '' : v)}>
                      <SelectTrigger data-testid="filter-country"><SelectValue placeholder="Tous les pays" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous les pays</SelectItem>
                        <SelectItem value="Belgium">Belgique</SelectItem>
                        <SelectItem value="Luxembourg">Luxembourg</SelectItem>
                        <SelectItem value="France">France</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('jobs.budgetRange')}</label>
                    <div className="flex gap-2">
                      <Input type="number" placeholder="Min" value={filters.budget_min} onChange={(e) => handleFilterChange('budget_min', e.target.value)} className="input-field" data-testid="filter-budget-min" />
                      <Input type="number" placeholder="Max" value={filters.budget_max} onChange={(e) => handleFilterChange('budget_max', e.target.value)} className="input-field" data-testid="filter-budget-max" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox id="urgent" checked={filters.is_urgent} onCheckedChange={(c) => handleFilterChange('is_urgent', c)} data-testid="filter-urgent" />
                    <label htmlFor="urgent" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-500" /> {t('jobs.urgentJobs')}
                    </label>
                  </div>

                  {/* Map legend */}
                  {viewMode === 'map' && (
                    <div className="pt-4 border-t space-y-3">
                      <p className="text-xs font-semibold text-[#374151] uppercase tracking-wide">Légende</p>
                      <div className="flex items-center gap-2">
                        <span className="w-4 h-4 rounded-full bg-[#1E3A8A] border-2 border-white shadow"></span>
                        <span className="text-sm text-[#374151]">Chantiers</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-4 h-4 rounded-full bg-[#EF4444] border-2 border-white shadow"></span>
                        <span className="text-sm text-[#374151]">Chantiers urgents</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox checked={showContractors} onCheckedChange={setShowContractors} />
                        <span className="flex items-center gap-2 text-sm text-[#374151]">
                          <span className="w-4 h-4 rounded-full bg-[#F59E0B] border-2 border-white shadow"></span>
                          Sous-traitants
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-10 h-10 animate-spin text-[#1E3A8A]" />
              </div>
            ) : viewMode === 'map' ? (
              /* ========= MAP VIEW ========= */
              <div className="h-[600px] rounded-xl overflow-hidden border border-[#1E3A8A]/10 shadow-lg" data-testid="jobs-map">
                <MapContainer center={[50.5, 4.5]} zoom={7} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
                  <TileLayer attribution={OSM_ATTRIBUTION} url={OSM_TILE_URL} />
                  <MarkerClusterGroup key={markers.length} {...CLUSTER_OPTIONS}>
                  {markers.map((marker, idx) => (
                    <Marker
                      key={`${marker.type}-${marker.data.id || idx}`}
                      position={[marker.lat, marker.lng]}
                      markerType={marker.type}
                      icon={marker.type === 'job' ? (marker.data.is_urgent ? urgentIcon : jobIcon) : contractorIcon}
                    >
                      <Popup>
                        <div className="min-w-[220px]">
                          {marker.type === 'job' ? (
                            <>
                              <h4 style={{ fontWeight: 'bold', color: '#1E3A8A', margin: '0 0 4px 0' }}>{marker.data.title}</h4>
                              <p style={{ fontSize: '13px', color: '#4B5563', margin: '0 0 4px 0' }}>{marker.data.city}, {marker.data.country}</p>
                              {marker.data.is_urgent && <span style={{ background: '#FEE2E2', color: '#DC2626', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>Urgent</span>}
                              <p style={{ fontSize: '13px', margin: '6px 0' }}>{marker.data.budget_min?.toLocaleString()}€ - {marker.data.budget_max?.toLocaleString()}€</p>
                              <div style={{ display: 'flex', gap: '4px', marginTop: '8px' }}>
                                <a href={`/jobs/${marker.data.id}`} style={{ flex: 1, display: 'block', textAlign: 'center', background: '#1E3A8A', color: 'white', padding: '6px', borderRadius: '4px', textDecoration: 'none', fontSize: '12px' }}>Voir</a>
                                {isOwner(marker.data) && (
                                  <>
                                    <a href={`/dashboard/edit-job/${marker.data.id}`} style={{ display: 'block', textAlign: 'center', background: '#F59E0B', color: 'white', padding: '6px 10px', borderRadius: '4px', textDecoration: 'none', fontSize: '12px' }}>Modifier</a>
                                  </>
                                )}
                              </div>
                            </>
                          ) : (
                            <>
                              <h4 style={{ fontWeight: 'bold', color: '#F59E0B', margin: '0 0 4px 0' }}>{marker.data.company_name}</h4>
                              <p style={{ fontSize: '13px', color: '#4B5563', margin: '0 0 4px 0' }}>{marker.data.city}</p>
                              <p style={{ fontSize: '13px' }}>Note: {marker.data.rating?.toFixed(1) || '0.0'}/5</p>
                              <a href={`/contractors/${marker.data.id}`} style={{ display: 'block', textAlign: 'center', background: '#F59E0B', color: 'white', padding: '6px', borderRadius: '4px', textDecoration: 'none', fontSize: '12px', marginTop: '8px' }}>Voir profil</a>
                            </>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                  </MarkerClusterGroup>
                  {markers.length > 0 && <MapBounds markers={markers} />}
                </MapContainer>
              </div>
            ) : jobs.length === 0 ? (
              /* ========= EMPTY STATE ========= */
              <Card className="p-12 text-center">
                <p className="text-[#4B5563]">{t('common.noResults')}</p>
              </Card>
            ) : (
              /* ========= LIST VIEW ========= */
              <div className="space-y-4">
                {jobs.map((raw) => {
                  const job = displayJob(raw, user);
                  const isExpired = isJobExpired(job);
                  return (
                  <Link to={`/jobs/${job.id}`} key={job.id}>
                    <Card className={`card-surface hover:shadow-md transition-shadow ${isExpired ? 'opacity-70 bg-[#F3F4F6]' : ''}`} data-testid={`job-item-${job.id}`}>
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              {(job.trade_categories && job.trade_categories.length > 0
                                ? job.trade_categories
                                : [job.trade_category]
                              ).filter(Boolean).map(tc => (
                                <Badge key={tc} className="badge-trade">{t(`trades.${tc}`) || tc}</Badge>
                              ))}
                              {job.is_urgent && <Badge className="badge-urgent">{t('common.urgent')}</Badge>}
                              {isExpired && <Badge className="bg-red-100 text-red-700 border border-red-300">Expiré</Badge>}
                            </div>
                            <h3 className={`font-heading font-bold text-xl mb-2 ${job.masked ? 'text-[#6B7280]' : 'text-[#111827]'}`} data-testid={`job-item-title-${job.id}`}>{job.title}</h3>
                            <p className="text-[#4B5563] mb-4 line-clamp-2">{job.description}</p>
                            <div className="flex flex-wrap gap-4 text-sm text-[#4B5563]">
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4 text-[#1E3A8A]" />
                                {job.postal_code && <span className="font-mono text-xs bg-[#1E3A8A]/10 text-[#1E3A8A] px-1 rounded">{job.country === 'Belgium' ? 'BE' : job.country === 'France' ? 'FR' : 'LU'}-{job.postal_code}</span>}
                                {job.city}, {job.country}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4 text-[#1E3A8A]" /> {job.duration}
                              </span>
                            </div>
                          </div>
                          <div className="text-right flex flex-col items-end gap-2">
                            <div>
                              <p className="text-sm text-[#4B5563] mb-1">{t('jobs.budget')}</p>
                              <p className="font-heading font-bold text-xl text-[#1E3A8A] flex items-center gap-1 justify-end">
                                <Euro className="w-5 h-5" />
                                {job.budget_min || job.budget_max 
                                  ? `${job.budget_min?.toLocaleString() || '—'} - ${job.budget_max?.toLocaleString() || '—'}`
                                  : 'Sur devis'}
                              </p>
                            </div>
                            <p className="text-xs text-[#4B5563]">{job.company_name}</p>
                            {/* Owner actions */}
                            {isOwner(job) && (
                              <div className="flex gap-2 mt-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-1 text-xs border-[#1E3A8A]/30 text-[#1E3A8A]"
                                  onClick={(e) => handleEdit(job.id, e)}
                                  data-testid={`edit-job-${job.id}`}
                                >
                                  <Pencil className="w-3 h-3" /> Modifier
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-1 text-xs border-red-300 text-red-600 hover:bg-red-50"
                                  onClick={(e) => handleDelete(job.id, e)}
                                  disabled={deletingId === job.id}
                                  data-testid={`delete-job-${job.id}`}
                                >
                                  {deletingId === job.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                                  Supprimer
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                  );
                })}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default JobsPage;
